import { useState } from 'react';
import api from '../../utils/axiosConfig';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import AttributeValueBadge from './AttributeValueBadge';
import { LUCIDE_ICON_OPTIONS } from '../../utils/periodizationValueSchema';

const SCHEMA_TYPES = ['numeric_scale', 'numeric_count', 'boolean', 'lookup', 'segmented_scale'];

export function defaultValueSchemaConfig(schemaType) {
  if (schemaType === 'boolean') {
    return {
      config_version: 1,
      storage: 'boolean',
      boolean: {
        true_label: 'Yes',
        false_label: 'No',
        true_color: '#22c55e',
        false_color: '#737380',
        true_icon: 'check',
        false_icon: 'x',
      },
      display: { show_icon: true, show_label: true, chart_value_source: 'numeric' },
    };
  }
  if (schemaType === 'lookup') {
    return {
      config_version: 1,
      storage: 'lookup_key',
      lookup: {
        options: [
          { key: 'low', label: 'Low', numeric_value: 1, color: '#22c55e', icon: 'arrow-down' },
          { key: 'medium', label: 'Medium', numeric_value: 2, color: '#eab308', icon: 'minus' },
          { key: 'high', label: 'High', numeric_value: 3, color: '#ef4444', icon: 'arrow-up' },
        ],
      },
      display: { show_icon: true, show_label: true, chart_value_source: 'numeric' },
    };
  }
  const min = schemaType === 'numeric_count' ? 1 : 0;
  const max = schemaType === 'numeric_count' ? 7 : 100;
  return {
    config_version: 1,
    storage: 'number',
    numeric: { min, max, step: 1, default: Math.round((min + max) / 2) },
    segments: [
      { up_to: Math.round(max * 0.4), color: '#ef4444', label: 'Low' },
      { up_to: Math.round(max * 0.7), color: '#eab308', label: 'Med' },
      { up_to: max, color: '#22c55e', label: 'High' },
    ],
    display: { show_icon: false, show_label: true, chart_value_source: 'numeric' },
  };
}

function apiPaths(mode) {
  if (mode === 'admin') {
    return {
      list: '/api/admin/periodization-catalog/value-schemas',
      create: '/api/admin/periodization-catalog/value-schemas',
      update: (id) => `/api/admin/periodization-catalog/value-schemas/${id}`,
    };
  }
  return {
    list: '/api/training/periodization/value-schemas',
    create: '/api/training/periodization/value-schemas',
    update: (id) => `/api/training/periodization/value-schemas/${id}`,
    duplicate: (id) => `/api/training/periodization/value-schemas/${id}/duplicate`,
  };
}

/**
 * @param {'user'|'admin'} mode
 * @param {boolean} allowEditGlobal — admin: edit built-in global schemas
 */
export default function ValueSchemaEditorForm({
  schemas = [],
  onReload,
  saving,
  setSaving,
  mode = 'user',
  allowEditGlobal = false,
  showList = true,
}) {
  const { t } = useLanguage();
  const paths = apiPaths(mode);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    schema_key: '',
    name: '',
    description: '',
    schema_type: 'numeric_scale',
    config: defaultValueSchemaConfig('numeric_scale'),
  });

  const globalSchemas = schemas.filter((s) => s.scope === 'global' && !s.is_archived);
  const userSchemas = schemas.filter((s) => s.scope === 'user' && !s.is_archived);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      schema_key: '',
      name: '',
      description: '',
      schema_type: 'numeric_scale',
      config: defaultValueSchemaConfig('numeric_scale'),
    });
  };

  const canEditSchema = (schema) => {
    if (!schema) return false;
    if (mode === 'admin' && allowEditGlobal && schema.scope === 'global') return true;
    return schema.scope === 'user';
  };

  const startEdit = (schema) => {
    if (!canEditSchema(schema)) return;
    setEditingId(schema.id);
    setForm({
      schema_key: schema.schema_key,
      name: schema.name,
      description: schema.description || '',
      schema_type: schema.schema_type,
      config: schema.config || defaultValueSchemaConfig(schema.schema_type),
    });
  };

  const saveSchema = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await api.put(paths.update(editingId), {
          name: form.name.trim(),
          description: form.description?.trim() || null,
          schema_type: form.schema_type,
          config: form.config,
        });
      } else {
        await api.post(paths.create, {
          schema_key: form.schema_key.trim(),
          name: form.name.trim(),
          description: form.description?.trim() || null,
          schema_type: form.schema_type,
          config: form.config,
        });
      }
      resetForm();
      await onReload?.();
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.valueSchema.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const updateLookupOption = (idx, field, val) => {
    setForm((prev) => {
      const options = [...(prev.config.lookup?.options || [])];
      options[idx] = { ...options[idx], [field]: val };
      return { ...prev, config: { ...prev.config, lookup: { options } } };
    });
  };

  const addLookupOption = () => {
    setForm((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        lookup: {
          options: [
            ...(prev.config.lookup?.options || []),
            { key: `opt_${Date.now()}`, label: 'New', numeric_value: 1, color: '#94a3b8', icon: 'circle' },
          ],
        },
      },
    }));
  };

  const removeLookupOption = (idx) => {
    setForm((prev) => {
      const options = (prev.config.lookup?.options || []).filter((_, i) => i !== idx);
      return { ...prev, config: { ...prev.config, lookup: { options } } };
    });
  };

  const updateSegment = (idx, field, val) => {
    setForm((prev) => {
      const segments = [...(prev.config.segments || [])];
      segments[idx] = { ...segments[idx], [field]: val };
      return { ...prev, config: { ...prev.config, segments } };
    });
  };

  const addSegment = () => {
    const max = form.config.numeric?.max ?? 100;
    setForm((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        segments: [...(prev.config.segments || []), { up_to: max, color: '#22c55e', label: '' }],
      },
    }));
  };

  const updateBooleanField = (field, val) => {
    setForm((prev) => ({
      ...prev,
      config: {
        ...prev.config,
        boolean: { ...prev.config.boolean, [field]: val },
      },
    }));
  };

  const schemaTypeLabel = (st) => {
    const k = `periodization.valueSchema.types.${st}`;
    const tr = t(k);
    return tr !== k ? tr : st;
  };

  const previewType = {
    value_schema: {
      schema_type: form.schema_type,
      config: form.config,
      schema_key: form.schema_key || 'preview',
      name: form.name || 'Preview',
    },
  };
  const previewValue =
    form.schema_type === 'lookup'
      ? form.config.lookup?.options?.[1]?.key
      : form.schema_type === 'boolean'
        ? true
        : form.config.numeric?.default;

  const numericLike =
    form.schema_type === 'numeric_scale' ||
    form.schema_type === 'numeric_count' ||
    form.schema_type === 'segmented_scale';

  return (
    <div className='space-y-4'>
      <div className='rounded-lg border border-border bg-card p-4 space-y-4'>
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <p className='text-sm font-medium text-foreground'>
            {editingId ? t('periodization.valueSchema.edit') : t('periodization.valueSchema.create')}
          </p>
          <AttributeValueBadge value={previewValue} type={previewType} />
        </div>

        <div className='grid gap-3 sm:grid-cols-2'>
          {!editingId && (
            <div>
              <Label className='text-xs'>{t('periodization.valueSchema.key')}</Label>
              <Input
                className='mt-1 font-mono text-xs'
                value={form.schema_key}
                onChange={(e) => setForm((p) => ({ ...p, schema_key: e.target.value }))}
                placeholder='my_scale'
              />
            </div>
          )}
          <div>
            <Label className='text-xs'>{t('periodization.valueSchema.name')}</Label>
            <Input className='mt-1' value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className='sm:col-span-2'>
            <Label className='text-xs'>{t('periodization.admin.schemaDescription')}</Label>
            <Input
              className='mt-1'
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div>
            <Label className='text-xs'>{t('periodization.assessmentManager.valueKind')}</Label>
            <Select
              value={form.schema_type}
              onValueChange={(v) => setForm((p) => ({ ...p, schema_type: v, config: defaultValueSchemaConfig(v) }))}
              disabled={!!editingId}
            >
              <SelectTrigger className='mt-1'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCHEMA_TYPES.map((st) => (
                  <SelectItem key={st} value={st}>
                    {schemaTypeLabel(st)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.schema_type === 'lookup' && (
          <div className='space-y-2 rounded-md border border-border/60 p-3'>
            <div className='flex items-center justify-between'>
              <p className='text-xs font-medium'>{t('periodization.valueSchema.lookupOptions')}</p>
              <Button type='button' size='sm' variant='outline' className='h-7 gap-1' onClick={addLookupOption}>
                <Plus className='h-3 w-3' />
                {t('periodization.admin.addOption')}
              </Button>
            </div>
            {(form.config.lookup?.options || []).map((opt, idx) => (
              <div key={idx} className='grid grid-cols-2 sm:grid-cols-6 gap-2 items-end'>
                <Input className='text-xs font-mono' placeholder='key' value={opt.key} onChange={(e) => updateLookupOption(idx, 'key', e.target.value)} />
                <Input className='text-xs' placeholder='label' value={opt.label} onChange={(e) => updateLookupOption(idx, 'label', e.target.value)} />
                <Input className='text-xs' type='number' value={opt.numeric_value} onChange={(e) => updateLookupOption(idx, 'numeric_value', parseInt(e.target.value, 10))} />
                <Input className='text-xs h-9' type='color' value={opt.color} onChange={(e) => updateLookupOption(idx, 'color', e.target.value)} />
                <Select value={opt.icon || ''} onValueChange={(v) => updateLookupOption(idx, 'icon', v)}>
                  <SelectTrigger className='h-9 text-xs'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LUCIDE_ICON_OPTIONS.map((ic) => (
                      <SelectItem key={ic} value={ic}>
                        {ic}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type='button' size='icon' variant='ghost' className='h-9 w-9' onClick={() => removeLookupOption(idx)}>
                  <Trash2 className='h-4 w-4 text-destructive' />
                </Button>
              </div>
            ))}
          </div>
        )}

        {form.schema_type === 'boolean' && (
          <div className='grid gap-3 sm:grid-cols-2 rounded-md border border-border/60 p-3'>
            <p className='sm:col-span-2 text-xs font-medium'>{t('periodization.admin.booleanLabels')}</p>
            {['true_label', 'false_label'].map((f) => (
              <div key={f}>
                <Label className='text-xs capitalize'>{f.replace('_', ' ')}</Label>
                <Input className='mt-1 text-xs' value={form.config.boolean?.[f] || ''} onChange={(e) => updateBooleanField(f, e.target.value)} />
              </div>
            ))}
            {['true_color', 'false_color'].map((f) => (
              <div key={f}>
                <Label className='text-xs'>{f}</Label>
                <Input className='mt-1 h-9' type='color' value={form.config.boolean?.[f] || '#000000'} onChange={(e) => updateBooleanField(f, e.target.value)} />
              </div>
            ))}
            {['true_icon', 'false_icon'].map((f) => (
              <div key={f}>
                <Label className='text-xs'>{f}</Label>
                <Select value={form.config.boolean?.[f] || ''} onValueChange={(v) => updateBooleanField(f, v)}>
                  <SelectTrigger className='mt-1 h-9 text-xs'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LUCIDE_ICON_OPTIONS.map((ic) => (
                      <SelectItem key={ic} value={ic}>
                        {ic}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}

        {numericLike && (
          <>
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-2'>
              {['min', 'max', 'step', 'default'].map((field) => (
                <div key={field}>
                  <Label className='text-xs capitalize'>{field}</Label>
                  <Input
                    type='number'
                    className='mt-1 text-xs'
                    value={form.config.numeric?.[field] ?? ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        config: {
                          ...p.config,
                          numeric: { ...p.config.numeric, [field]: parseFloat(e.target.value) },
                        },
                      }))
                    }
                  />
                </div>
              ))}
            </div>
            <div className='space-y-2 rounded-md border border-border/60 p-3'>
              <div className='flex items-center justify-between'>
                <p className='text-xs font-medium'>{t('periodization.admin.colorSegments')}</p>
                <Button type='button' size='sm' variant='outline' className='h-7 gap-1' onClick={addSegment}>
                  <Plus className='h-3 w-3' />
                  {t('periodization.admin.addSegment')}
                </Button>
              </div>
              {(form.config.segments || []).map((seg, idx) => (
                <div key={idx} className='grid grid-cols-3 sm:grid-cols-4 gap-2 items-end'>
                  <div>
                    <Label className='text-xs'>{t('periodization.admin.segmentUpTo')}</Label>
                    <Input
                      type='number'
                      className='mt-1 text-xs'
                      value={seg.up_to}
                      onChange={(e) => updateSegment(idx, 'up_to', parseFloat(e.target.value))}
                    />
                  </div>
                  <div>
                    <Label className='text-xs'>{t('periodization.admin.segmentLabel')}</Label>
                    <Input className='mt-1 text-xs' value={seg.label || ''} onChange={(e) => updateSegment(idx, 'label', e.target.value)} />
                  </div>
                  <div>
                    <Label className='text-xs'>{t('periodization.admin.segmentColor')}</Label>
                    <Input className='mt-1 h-9' type='color' value={seg.color || '#94a3b8'} onChange={(e) => updateSegment(idx, 'color', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        <div className='flex gap-2 justify-end'>
          {editingId && (
            <Button type='button' variant='ghost' onClick={resetForm}>
              {t('common.cancel')}
            </Button>
          )}
          <Button type='button' onClick={saveSchema} disabled={saving}>
            {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('common.save')}
          </Button>
        </div>
      </div>

      {showList && (
        <>
          <SchemaList
            title={t('periodization.valueSchema.global')}
            items={globalSchemas}
            onEdit={allowEditGlobal ? startEdit : null}
            editingId={editingId}
            t={t}
            schemaTypeLabel={schemaTypeLabel}
          />
          {mode === 'user' && (
            <SchemaList
              title={t('periodization.valueSchema.yours')}
              items={userSchemas}
              onEdit={startEdit}
              editingId={editingId}
              t={t}
              schemaTypeLabel={schemaTypeLabel}
            />
          )}
        </>
      )}
    </div>
  );
}

function SchemaList({ title, items, onEdit, editingId, t, schemaTypeLabel }) {
  if (!items.length) return null;
  return (
    <div>
      <p className='text-xs font-medium text-muted-foreground mb-2'>{title}</p>
      <ul className='space-y-1'>
        {items.map((s) => (
          <li
            key={s.id}
            className={`flex justify-between items-center gap-2 text-sm py-2 px-2 rounded-md border ${
              editingId === s.id ? 'border-primary bg-primary/5' : 'border-border/60'
            }`}
          >
            <div className='min-w-0'>
              <p className='font-medium truncate'>{s.name}</p>
              <p className='text-[10px] text-muted-foreground font-mono'>{s.schema_key}</p>
            </div>
            <div className='flex items-center gap-2 shrink-0'>
              <Badge variant='outline' className='text-[10px]'>
                {schemaTypeLabel(s.schema_type)}
              </Badge>
              <AttributeValueBadge
                value={
                  s.schema_type === 'lookup'
                    ? s.config?.lookup?.options?.[1]?.key
                    : s.schema_type === 'boolean'
                      ? true
                      : s.config?.numeric?.default
                }
                type={{ value_schema: s }}
                compact
              />
              {onEdit && (
                <Button type='button' size='sm' variant='outline' className='h-7' onClick={() => onEdit(s)}>
                  {t('common.edit')}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
